/**
 * #2603 — one rail owns bottom-right so every floating pill stacks beside
 * Ask Crosbot instead of guessing its width with a hard-coded offset. The
 * rail is a single fixed flex row, portalled to document.body once from
 * AppShell; a page registers a pill through useFloatingPillRail() /
 * FloatingPillSlot and declares only that it is a pill, never where it sits.
 *
 * Layout is flex-row-reverse so the rail packs from its right edge; Ask
 * Crosbot carries `order-first`, which pins it to that right edge no matter
 * which pill mounts first, so it stays rightmost the way the old per-caller
 * offsets tried to guarantee by convention.
 *
 * #3214: the rail also reserves its band. A floating pill over a scrolling
 * page covers whatever scrolls beneath it, and at phone width that was a
 * page's own primary action (Extract items on the Projects Review tab). So
 * the rail measures the viewport band it occupies (its top edge down to the
 * bottom of the screen, plus a gap) and publishes it as --pill-rail-band on
 * the document root; AppShell adds that band after the page at every width,
 * so the last control can scroll clear without shrinking the scrolling region
 * or leaving a permanent strip. An empty rail (no pill for this role, the
 * analyst page, the panel open) has no height, so pillRailBand gives 0 and it
 * reserves nothing. Other layouts may read the same variable; no page
 * declares anything.
 *
 * `document` is undefined in the workerd test pool (no DOM), and refs to
 * host elements resolve to null under react-test-renderer's default (no
 * createNodeMock). Both paths fall back to rendering inline rather than
 * portalling, so a component test that mounts a pill still finds it without
 * needing a real browser; production always has document, so the portal is
 * the path that actually runs there.
 */
import { createContext, useCallback, useContext, useLayoutEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

const FloatingPillRailContext = createContext<HTMLDivElement | null>(null);

/** The CSS variable the rail publishes on the document root (#3214). */
export const PILL_RAIL_BAND_VAR = "--pill-rail-band";
/** Clear space between the page's last row and the top of the pills. */
export const PILL_RAIL_BAND_GAP_PX = 8;

/**
 * #5114: the band the pills alone occupy, published beside --pill-rail-band.
 * A lifted bottom toast stands on this one; below lg --pill-rail-band also
 * covers every bottom toast showing, so whatever keeps clear of it
 * (AppShell's end spacer, the Records grid fit) keeps clear of them too.
 */
export const PILL_RAIL_PILLS_VAR = "--pill-rail-pills";

/**
 * #5114: set on a bottom toast (components/BottomToast.tsx) whose span across
 * the screen meets the rail's, below lg. BOTTOM_TOAST_CLASS stands it on the
 * pills' band then; otherwise it keeps its own offset.
 */
export const BOTTOM_TOAST_LIFTED_ATTR = "data-lifted";

/** Tailwind's lg: from here no toast lifts and the band is the pills' alone. */
const LG_QUERY = "(min-width: 64rem)";

/**
 * The band a rail occupies, in px: from its top edge to the bottom of the
 * viewport (which carries the rail's bottom offset and the safe-area inset),
 * plus the gap. An empty rail has no height and reserves nothing: measured at
 * 390x844 with the Analyst panel open, it is a 0x0 box at y 824 (its own
 * 20 px offset), which would give 28 px by position alone, and this returns 0.
 */
export function pillRailBand(railRect: { top: number; height: number }, viewportHeight: number): number {
  if (railRect.height <= 0) return 0;
  return Math.max(0, Math.ceil(viewportHeight - railRect.top + PILL_RAIL_BAND_GAP_PX));
}

/**
 * #5114 TOASTCROSBOT: whether a bottom toast's span across the screen comes
 * within the band gap of the rail's, so the two would meet if both sat on
 * their own bottom offsets. A centred toast's span does not depend on how
 * high it sits, so lifting it cannot change the answer. An empty rail (0x0)
 * meets nothing.
 */
export function toastMeetsRail(
  toast: { left: number; right: number },
  rail: { left: number; right: number; width: number; height: number },
): boolean {
  if (rail.width <= 0 || rail.height <= 0) return false;
  return toast.left < rail.right + PILL_RAIL_BAND_GAP_PX && rail.left - PILL_RAIL_BAND_GAP_PX < toast.right;
}

/**
 * Keeps --pill-rail-band in step with the rail's real size and position.
 * #5114: below lg each bottom toast is lifted onto the pills' band when its
 * span meets the rail's, and the band covers every toast showing, lifted or
 * not, so a page's last row can still scroll clear of them. A layout effect,
 * so a toast that mounts already needing the lift never paints a frame on
 * the pill.
 */
function usePillRailBand(rail: HTMLDivElement | null, toasts: ReadonlySet<HTMLElement>) {
  useLayoutEffect(() => {
    if (!rail || typeof document === "undefined" || typeof rail.getBoundingClientRect !== "function") return;
    const root = document.documentElement;
    const measure = () => {
      const railRect = rail.getBoundingClientRect();
      const pills = pillRailBand(railRect, window.innerHeight);
      root.style.setProperty(PILL_RAIL_PILLS_VAR, `${pills}px`);
      const belowLg = typeof window.matchMedia === "function" && !window.matchMedia(LG_QUERY).matches;
      let band = pills;
      for (const toast of toasts) {
        toast.toggleAttribute(BOTTOM_TOAST_LIFTED_ATTR, belowLg && toastMeetsRail(toast.getBoundingClientRect(), railRect));
        // Read after the lift: a lifted toast stands on the band just set.
        if (belowLg) band = Math.max(band, pillRailBand(toast.getBoundingClientRect(), window.innerHeight));
      }
      root.style.setProperty(PILL_RAIL_BAND_VAR, `${band}px`);
    };
    measure();
    // A pill mounting or leaving resizes the rail; a viewport change (rotation,
    // the iOS toolbar collapsing) moves it without resizing it.
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(rail);
    for (const toast of toasts) ro?.observe(toast);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      root.style.removeProperty(PILL_RAIL_BAND_VAR);
      root.style.removeProperty(PILL_RAIL_PILLS_VAR);
    };
  }, [rail, toasts]);
}

/** How a bottom toast hands the rail its element; the cleanup takes it back (#5114). */
type RegisterBottomToast = (el: HTMLElement | null) => void | (() => void);
const BottomToastContext = createContext<RegisterBottomToast | null>(null);

export function FloatingPillRailProvider({
  presentationMode = false,
  children,
}: {
  presentationMode?: boolean;
  children: ReactNode;
}) {
  const [rail, setRail] = useState<HTMLDivElement | null>(null);
  const [toasts, setToasts] = useState<ReadonlySet<HTMLElement>>(() => new Set());
  usePillRailBand(rail, toasts);
  // A ref callback with a cleanup (React 19): stable, so a toast registers once per mount.
  const registerToast = useCallback<RegisterBottomToast>((el) => {
    if (!el) return;
    setToasts((prev) => new Set(prev).add(el));
    return () =>
      setToasts((prev) => {
        const next = new Set(prev);
        next.delete(el);
        return next;
      });
  }, []);
  const railNode = (
    <div
      ref={setRail}
      className={`fixed bottom-[calc(1.25rem+env(safe-area-inset-bottom))] z-40 flex flex-row-reverse items-center gap-3 right-5 lg:bottom-6 print:hidden ${
        presentationMode ? "lg:right-6" : ""
      }`}
    />
  );
  return (
    <FloatingPillRailContext.Provider value={rail}>
      <BottomToastContext.Provider value={registerToast}>
        {children}
        {typeof document !== "undefined" ? createPortal(railNode, document.body) : railNode}
      </BottomToastContext.Provider>
    </FloatingPillRailContext.Provider>
  );
}

/** The rail's portal target, or null until the provider has mounted it. */
export function useFloatingPillRail() {
  return useContext(FloatingPillRailContext);
}

/**
 * #5114: the ref a bottom toast passes to its element, so the rail can lift
 * it and count it in the band. Null outside the provider (Display Mode mounts
 * its own update toast outside the shell): no rail, so the toast keeps its
 * own offset and nothing reads the band.
 */
export function useBottomToastRef() {
  return useContext(BottomToastContext);
}

/**
 * Portals a pill into the rail. Renders the pill inline, in its own spot in
 * the tree, until the rail exists (first tick, or no DOM available) so a
 * pill never simply fails to render.
 */
export function FloatingPillSlot({ children }: { children: ReactNode }) {
  const rail = useFloatingPillRail();
  if (!rail) return <>{children}</>;
  return createPortal(children, rail);
}
